import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { BackButton } from '../../components/back-button'
import { z } from 'zod'
import {
  CaptchaModal,
  apiClient,
  upsertSavedAccount,
  ApiClientError,
  getSupabase,
} from '@dogdisk/shared'
import { MobileScreen } from '../../components/mobile-screen'

type LoginResp = {
  next_step: 'ok' | '2fa_required'
  user: { user_id: string; username: string; avatar_url: string | null }
  access_token?: string
  refresh_token?: string
  mfa_session_id?: string
}

const schema = z.object({
  identifier: z.string().min(1, '请输入账号'),
  password: z.string().min(8, '密码至少 8 位'),
})

export function LoginPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next') ?? '/'

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [captchaOpen, setCaptchaOpen] = useState(false)
  const [pending, setPending] = useState<{ identifier: string; password: string } | null>(null)
  // 2026-05-28 dg-fix-user2 §4 梯度 captcha:CAPTCHA_REQUIRED.details.required_count → modal 弹 N 张
  const [requiredCount, setRequiredCount] = useState(1)

  // 2026-05-29 fO6Sn 拆独立 route 后:fO6Sn 内 row click 跳 /auth/login?prefill_user_id=…
  // 本页读 query 从 localStorage 取 username + password 自动填,等用户主动点登录确认。
  useEffect(() => {
    const pid = params.get('prefill_user_id')
    if (!pid) return
    try {
      const raw = window.localStorage.getItem('saved_accounts')
      if (!raw) return
      const list = JSON.parse(raw) as Array<{ user_id: string; username: string; password: string }>
      const hit = list.find((a) => a.user_id === pid)
      if (hit) {
        setIdentifier(hit.username)
        setPassword(hit.password)
      }
    } catch {
      /* localStorage 损坏忽略 */
    }
  }, [params])

  const submitWithCaptcha = async (vals: { identifier: string; password: string }, captchaTokens?: string[]) => {
    setBusy(true)
    setErrMsg(null)
    try {
      const parsed = schema.parse(vals)
      const res = await apiClient.post<LoginResp>('/gapi/public/auth/login', {
        account: parsed.identifier,
        password: parsed.password,
        captcha_tokens: captchaTokens ?? [],
      })
      if (res.next_step === '2fa_required') {
        navigate(`/auth/2fa?pid=${encodeURIComponent(res.mfa_session_id ?? '')}&next=${encodeURIComponent(next)}`)
        return
      }
      if (res.access_token && res.refresh_token) {
        await getSupabase().auth.setSession({
          access_token: res.access_token,
          refresh_token: res.refresh_token,
        })
      }
      // 2026-05-28 owner 决策推翻 §8.9 "不存密码":明文存账号+密码到 localStorage。
      // 风险:XSS 可拖库 localStorage 明文密码;owner 已知晓权衡。
      upsertSavedAccount({
        user_id: res.user.user_id,
        username: res.user.username,
        avatar_url: res.user.avatar_url,
        password: parsed.password,
        last_login_at: new Date().toISOString(),
      })
      navigate(next)
    } catch (e) {
      if (e instanceof ApiClientError && e.code === 'CAPTCHA_REQUIRED') {
        const req = (e.details as { required_count?: number } | undefined)?.required_count ?? 1
        setRequiredCount(req)
        setPending(vals)
        setCaptchaOpen(true)
        return
      }
      setErrMsg(e instanceof Error ? e.message : '登录失败')
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void submitWithCaptcha({ identifier, password })
  }

  return (
    <MobileScreen>
      <main data-testid="o5t51" className="flex flex-1 flex-col">
        {/* Header: 返回 + 居中标题 + 44px 占位 */}
        <div className="flex items-center gap-3 px-6 pb-6 pt-[max(10px,env(safe-area-inset-top))]">
          <BackButton />
          <h1 className="flex-1 text-center text-[32px] font-bold text-[#1A1A1A]">登录</h1>
          <div className="h-11 w-11" />
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-2">
              <span className="text-[22px] font-semibold text-[#1A1A1A]">账号</span>
              <input
                data-testid="o5t51-account-input"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="用户名 / 邮箱 / 手机号"
                autoComplete="username"
                className="h-[50px] rounded-xl bg-[#F6F7F8] px-4 text-[17px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[22px] font-semibold text-[#1A1A1A]">密码</span>
              <input
                data-testid="o5t51-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                autoComplete="current-password"
                className="h-[50px] rounded-xl bg-[#F6F7F8] px-4 text-[17px] outline-none placeholder:text-[#B4B7BC] focus:bg-[#EBEDEF]"
              />
            </label>
          </div>

          {errMsg && <p className="text-[13px] text-[#FF6B6B]">{errMsg}</p>}

          <div className="flex flex-col items-center gap-4">
            <button
              data-testid="o5t51-login-btn"
              type="submit"
              disabled={busy || !identifier || !password}
              className="h-12 w-full rounded-xl bg-[#19caad] text-lg font-semibold text-white active:bg-[#14a18b] disabled:opacity-60"
            >
              {busy ? '登录中…' : '登录'}
            </button>
            <Link
              data-testid="o5t51-saved-link"
              to="/auth/login/saved"
              className="text-lg font-semibold text-[#19caad]"
            >
              → 使用已保存账号
            </Link>
            <div className="flex w-full items-center justify-between px-1">
              <Link
                data-testid="o5t51-forgot-link"
                to="/auth/forgot-password"
                className="text-[15px] font-semibold text-[#19caad]"
              >
                忘记密码?
              </Link>
              <Link
                data-testid="o5t51-register-link"
                to="/auth/register"
                className="text-[15px] font-semibold text-[#19caad]"
              >
                注册账号
              </Link>
            </div>
          </div>
        </form>
      </main>

      <CaptchaModal
        open={captchaOpen}
        onClose={() => setCaptchaOpen(false)}
        requiredCount={requiredCount}
        onVerified={(tokens) => {
          // 验证成功后 modal 不再自调 onClose,由本处关闭(2026-06-06 captcha fix)
          setCaptchaOpen(false)
          if (pending) void submitWithCaptcha(pending, tokens)
        }}
      />
    </MobileScreen>
  )
}
